import { HealthInsurerView } from '@/features/HealthInsurerView'
import { BasePage } from '../BasePage'
import { useNavigate, useParams } from 'react-router-dom'
import { useFetch } from '@/hooks/useFetch'
import { useEffect, useRef, useState } from 'react'
import { css } from '@emotion/react'
import { MainTitle } from '@/components/title/MainTitle'
import { BaseButton } from '@/components/button/BaseButton'
import { HealthInsurerDetail } from '@/types/HealthInsurerDetail'
import { ShowHistoryConfirmModal } from '@/features/ShowHistoryConfirmModal'
import { ConfirmModal } from '@/components/modal/ConfirmModal'
import { showError } from '@/shared/toast'
import { compareDateYmd } from '@/utils/dateUtils'
import { HistorySelect } from '@/components/select/HistorySelect'
import { useSetAtom, useAtom } from 'jotai'
import { isLoadingAtom } from '@/store/loading'
import { PUT } from '@/api/api'
import { showSuccess } from '@/shared/toast'
import { authAtom } from '@/store/auth/authAtom'
import { DetailMessage } from '@/components/message/Component'
import { BackButton } from '@/components/button/BackButton'
import { DetailViewContainer } from '@/components/detail/DetailViewContainer'
import { ROLE } from '@/constants/roles'
import { tableName } from '@/types/tableName'

const DETAIL_PAGE_WIDTH = 918

const pageContainerStyle = css`
  display: flex;
  flex-direction: column;
`

const pageHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 918px;
  max-width: 100%;
  margin-bottom: 12px;
`

const headerLeftStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const buttonAreaStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 918px;
  max-width: 100%;
  margin-top: 24px;
`

const leftButtonsStyle = css`
  display: flex;
  gap: 16px;
`
export const HealthInsurerDetailPage = () => {
  const params = useParams()
  const [id, setId] = useState<string>(params.id || '')
  const navigate = useNavigate()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [actionModal, setActionModal] = useState<{
    type: 'obsolete' | 'restore' | null
    data: HealthInsurerDetail | null
  }>({ type: null, data: null })
  const setLoading = useSetAtom(isLoadingAtom)
  const [user, setUser] = useAtom(authAtom)
  const isFirstRender = useRef(true)

  const { data, error, refetch } = useFetch<HealthInsurerDetail>(
    `/api/health-insurers/${id}`,
  )

  useEffect(() => {
    setId(params.id || '')
  }, [params.id])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (!id) {
      return
    }
    refetch()
  }, [id])

  useEffect(() => {
    if (error) {
      showError(
        '保険者情報の取得に失敗しました。時間をおいて再度お試しください。',
      )
    }
  }, [error])
  const handleObsoleteClick = (data?: HealthInsurerDetail) => {
    if (!data) {
      return
    }
    setActionModal({ type: 'obsolete', data })
  }

  const handleRestoreClick = (data?: HealthInsurerDetail) => {
    if (!data) {
      return
    }
    setActionModal({ type: 'restore', data })
  }

  const executeObsolete = () => {
    if (!actionModal.data) return
    setLoading(true)
    setActionModal({ type: null, data: null })

    PUT(`/api/obsolete/health-insurers/${actionModal.data.id}`)
      .then(() => {
        showSuccess('保険者を廃止しました。')
        refetch()
      })
      .catch(() => {
        showError('保険者の廃止に失敗しました。')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const executeRestore = () => {
    if (!actionModal.data) return
    setLoading(true)
    setActionModal({ type: null, data: null })

    PUT(`/api/restore/health-insurers/${actionModal.data.id}`)
      .then(() => {
        showSuccess('保険者を復元しました。')
        refetch()
      })
      .catch(() => {
        showError('保険者の復元に失敗しました。')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleClick = (data?: HealthInsurerDetail) => {
    if (!data) {
      return
    }

    if (compareDateYmd(data.startDate, new Date()) <= 0) {
      setShowConfirmModal(true)
    } else {
      navigate(`/health-insurers/${data.id}/edit`)
    }
  }
  return (
    <>
      <BasePage title="保険者詳細">
        <div css={pageContainerStyle}>
          <div css={pageHeaderStyle}>
            <div css={headerLeftStyle}>
              <BackButton to="/health-insurers" />
              <MainTitle>保険者詳細</MainTitle>
            </div>
            {data && (
              <HistorySelect
                key={data?.insurerCode}
                onChange={(nextId) => {
                  setId(nextId)
                }}
                tableName={tableName.HEALTH_INSURERS}
                id={params.id ?? ''}
                initialValue={id}
              />
            )}
          </div>
          {data && <DetailMessage data={data} width={DETAIL_PAGE_WIDTH} />}
          {data && (
            <div
              css={css`
                margin-top: ${data.startDatePending || data.startDateCurr
                  ? '0'
                  : '12px'};
              `}
            >
              <DetailViewContainer width={DETAIL_PAGE_WIDTH}>
                <HealthInsurerView data={data} />
              </DetailViewContainer>
            </div>
          )}
          {data && (
            <div css={buttonAreaStyle}>
              <div css={leftButtonsStyle}>
                {compareDateYmd(data.startDate, new Date()) > 0 && (
                  <>
                    <BaseButton
                      size="lg"
                      color="dangerOutline"
                      onClick={() =>
                        handleObsoleteClick(data ? data : undefined)
                      }
                      disabled={data?.isObsolete === '1'}
                    >
                      廃止
                    </BaseButton>
                    {data.isObsolete === '1' &&
                      (user?.role === ROLE.ADMIN ||
                        user?.role === ROLE.APPROVER) && (
                        <BaseButton
                          size="lg"
                          color="accentOutline"
                          onClick={() =>
                            handleRestoreClick(data ? data : undefined)
                          }
                          disabled={data?.isObsolete !== '1'}
                        >
                          復元
                        </BaseButton>
                      )}
                  </>
                )}
              </div>
              <BaseButton
                size="lg"
                color="outline"
                onClick={() => handleClick(data ? data : undefined)}
                disabled={
                  (data &&
                    data.startDateCurr &&
                    compareDateYmd(data.startDate, data.startDateCurr) < 0) ||
                  (data &&
                    data.isObsolete === '1' &&
                    data.startDateCurr &&
                    compareDateYmd(data.startDate, data.startDateCurr) === 0) ||
                  (data &&
                    data.startDatePending &&
                    compareDateYmd(data.startDate, data.startDatePending) !==
                      0) ||
                  undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  css={css`
                    width: 16px;
                    height: 16px;
                    fill: currentColor;
                  `}
                >
                  <path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160L0 416c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7-14.3 32-32 32L96 448c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 64z" />
                </svg>
                編集
              </BaseButton>
            </div>
          )}
        </div>
      </BasePage>
      {showConfirmModal && (
        <ShowHistoryConfirmModal
          displayText="healthInsurer"
          onConfirm={() => {
            navigate(`/health-insurers/${id}/edit`, {
              state: { insurer: data },
            })
            setShowConfirmModal(false)
          }}
          onCancel={() => {
            setShowConfirmModal(false)
          }}
        />
      )}
      {actionModal.type === 'obsolete' && actionModal.data && (
        <ConfirmModal
          title="廃止"
          titleColor="red"
          message={
            <>
              保険者番号：{actionModal.data.insurerCode}
              {actionModal.data.branchCode &&
                ` 保険者管理枝番：${actionModal.data.branchCode}`}
              <br />
              保険者名：{actionModal.data.insurerName}
              <br />
              <br />
              この保険者を廃止しますか？
            </>
          }
          confirmLabel="廃止する"
          cancelLabel="キャンセル"
          confirmColor="danger"
          onConfirm={executeObsolete}
          onCancel={() => setActionModal({ type: null, data: null })}
        />
      )}
      {actionModal.type === 'restore' && actionModal.data && (
        <ConfirmModal
          title="確認"
          message={
            <>
              保険者番号：{actionModal.data.insurerCode}
              {actionModal.data.branchCode &&
                ` 保険者管理枝番：${actionModal.data.branchCode}`}
              <br />
              保険者名：{actionModal.data.insurerName}
              <br />
              <br />
              この保険者を復元しますか？
            </>
          }
          confirmLabel="復元する"
          cancelLabel="キャンセル"
          onConfirm={executeRestore}
          onCancel={() => setActionModal({ type: null, data: null })}
        />
      )}
    </>
  )
}
